import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const countries = [
  { code: "+1", country: "US", name: "United States" },
  { code: "+1", country: "CA", name: "Canada" },
  { code: "+44", country: "GB", name: "United Kingdom" },
  { code: "+91", country: "IN", name: "India" },
  { code: "+61", country: "AU", name: "Australia" },
  { code: "+49", country: "DE", name: "Germany" },
  { code: "+33", country: "FR", name: "France" },
  { code: "+971", country: "AE", name: "United Arab Emirates" },
  { code: "+43", country: "AT", name: "Austria" },
  { code: "+880", country: "BD", name: "Bangladesh" },
  { code: "+32", country: "BE", name: "Belgium" },
  { code: "+55", country: "BR", name: "Brazil" },
  { code: "+41", country: "CH", name: "Switzerland" },
  { code: "+86", country: "CN", name: "China" },
  { code: "+420", country: "CZ", name: "Czech Republic" },
  { code: "+45", country: "DK", name: "Denmark" },
  { code: "+20", country: "EG", name: "Egypt" },
  { code: "+34", country: "ES", name: "Spain" },
  { code: "+358", country: "FI", name: "Finland" },
  { code: "+852", country: "HK", name: "Hong Kong" },
  { code: "+62", country: "ID", name: "Indonesia" },
  { code: "+353", country: "IE", name: "Ireland" },
  { code: "+39", country: "IT", name: "Italy" },
  { code: "+81", country: "JP", name: "Japan" },
  { code: "+254", country: "KE", name: "Kenya" },
  { code: "+82", country: "KR", name: "South Korea" },
  { code: "+52", country: "MX", name: "Mexico" },
  { code: "+60", country: "MY", name: "Malaysia" },
  { code: "+234", country: "NG", name: "Nigeria" },
  { code: "+31", country: "NL", name: "Netherlands" },
  { code: "+47", country: "NO", name: "Norway" },
  { code: "+64", country: "NZ", name: "New Zealand" },
  { code: "+63", country: "PH", name: "Philippines" },
  { code: "+92", country: "PK", name: "Pakistan" },
  { code: "+48", country: "PL", name: "Poland" },
  { code: "+351", country: "PT", name: "Portugal" },
  { code: "+966", country: "SA", name: "Saudi Arabia" },
  { code: "+46", country: "SE", name: "Sweden" },
  { code: "+65", country: "SG", name: "Singapore" },
  { code: "+66", country: "TH", name: "Thailand" },
  { code: "+84", country: "VN", name: "Vietnam" },
  { code: "+27", country: "ZA", name: "South Africa" },
];

const CountryCodeSelect = React.forwardRef(({ 
  value, 
  onChange, 
  className,
  ...props 
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedCountry = countries.find(c => c.code === value) || countries[0];

  const filteredCountries = countries.filter(country => 
    country.name.toLowerCase().includes(search.toLowerCase()) ||
    country.code.includes(search) ||
    country.country.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (country) => {
    onChange(country.code);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div ref={dropdownRef} className={cn("relative", className)} {...props}>
      <button
        ref={ref}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 h-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 transition-colors min-w-[100px]"
        data-testid="country-code-select"
      >
        <span className="text-sm font-medium">{selectedCountry.code}</span>
        <span className="text-xs text-gray-400">{selectedCountry.country}</span>
        <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-[#1a2a3a] border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-itmunch-cyan"
                data-testid="country-search-input"
              />
            </div>
          </div>

          {/* Country List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country, index) => (
                <button
                  key={`${country.code}-${country.country}-${index}`}
                  type="button"
                  onClick={() => handleSelect(country)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/10 transition-colors",
                    selectedCountry.code === country.code && selectedCountry.country === country.country && "bg-itmunch-cyan/10"
                  )}
                  data-testid={`country-option-${country.country}`}
                >
                  <span className="text-sm font-medium text-itmunch-cyan w-12">{country.code}</span>
                  <span className="text-xs text-gray-400 w-8">{country.country}</span>
                  <span className="text-sm text-white truncate">{country.name}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-gray-400 text-sm">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

CountryCodeSelect.displayName = "CountryCodeSelect";

export { CountryCodeSelect, countries };
